import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, Megaphone, Trophy, PenNib, ShieldCheck, Info, Medal } from "@phosphor-icons/react";
import api, { track } from "../api";

const TIER_COLOR = {
  Bronze: "border-amber-700/50 text-amber-500", Silver: "border-slate-400/40 text-slate-300",
  Gold: "border-ff-gold/50 text-ff-gold", Elite: "border-cyan-500/40 text-cyan-300",
  Champion: "border-purple-500/40 text-purple-300", Legacy: "border-ff-brass/50 text-ff-brass",
};

export const BadgeGrid = ({ badges, testid }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid={testid}>
    {(badges || []).map((b) => (
      <div key={b.verification_id} data-testid={`badge-${b.verification_id}`} className="ff-panel rounded-md p-4">
        <div className="flex items-start justify-between gap-2">
          <Medal size={26} weight="duotone" className="text-ff-gold" />
          <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${TIER_COLOR[b.tier] || "border-ff-brass/40 text-ff-brass"}`}>{b.tier}</span>
        </div>
        <h4 className="mt-2 font-display text-base font-bold text-ff-parchment">{b.name}</h4>
        <p className="text-xs text-ff-brass">{b.sponsor} · {b.category}</p>
        <p className="mt-2 text-sm text-ff-muted">{b.public_description}</p>
        <p className="mt-2 text-[11px] text-ff-muted/80">{b.rarity_example}</p>
        <p className="mt-2 rounded-sm bg-ff-bg px-2 py-1 text-[10px] uppercase tracking-wider text-ff-brass" data-testid="badge-preview-label">{b.label}</p>
      </div>
    ))}
  </div>
);

const FormatIcon = ({ name }) => {
  if (name === "Frontier Championship") return <Trophy size={24} weight="duotone" className="text-ff-gold" />;
  if (name === "Creator Commission") return <PenNib size={24} weight="duotone" className="text-ff-gold" />;
  return <Megaphone size={24} weight="duotone" className="text-ff-gold" />;
};

export default function Sponsors() {
  React.useEffect(() => { track("sponsor_page_view"); }, []);
  const [data, setData] = useState(null);
  const [badges, setBadges] = useState(null);

  useEffect(() => {
    api.get("/commissioned/public").then((r) => setData(r.data)).catch(() => {});
    api.get("/commissioned/badges/preview").then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-ff-bg text-ff-parchment" data-testid="sponsors-page">
      <header className="sticky top-0 z-40 border-b border-ff-brass/25 bg-ff-bg/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" data-testid="sponsors-home-link" className="flex items-center gap-2">
            <Compass size={28} weight="duotone" className="text-ff-gold animate-compass" />
            <span className="font-display text-xl font-bold tracking-tight text-ff-gold">Aurumward</span>
          </Link>
          <Link to="/login" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted transition-colors hover:border-ff-gold hover:text-ff-gold">
            Enter
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-12">
        <div className="inline-flex items-center gap-2 rounded-full border border-ff-brass/30 px-3 py-1 text-xs uppercase tracking-widest text-ff-brass" data-testid="sponsors-badge">
          <Info size={14} /> Preview · Under development
        </div>
        <h1 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
          Commissioned Expeditions
        </h1>
        <p className="mt-4 text-lg text-ff-gold" data-testid="sponsors-tagline">{data?.tagline}</p>
        <p className="mt-2 max-w-2xl text-base text-ff-muted">{data?.principle}</p>

        <p className="mt-8 max-w-3xl text-base leading-relaxed text-ff-parchment/90" data-testid="sponsors-summary">
          {data?.summary}
        </p>

        <h2 className="mt-12 text-lg font-semibold text-ff-gold">Three commissioned formats</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-3" data-testid="sponsors-formats">
          {(data?.formats || []).map((f) => (
            <div key={f.name} className="ff-panel rounded-md p-5">
              <FormatIcon name={f.name} />
              <h3 className="mt-3 font-display text-lg font-bold">{f.name}</h3>
              <p className="mt-2 text-sm text-ff-muted">{f.desc}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-12 flex items-center gap-2 text-lg font-semibold text-ff-gold">
          <ShieldCheck size={20} weight="duotone" /> How we protect the experience
        </h2>
        <ul className="mt-4 space-y-2" data-testid="sponsors-guardrails">
          {(data?.sponsor_guardrails || []).map((g, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-ff-parchment/90">
              <span className="mt-1 text-ff-brass">◆</span> {g}
            </li>
          ))}
        </ul>

        {badges && (
          <section className="mt-14" data-testid="sponsors-badges-section">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold">
              <Medal size={20} weight="duotone" /> {badges.title}
            </h2>
            <p className="mt-2 max-w-3xl text-sm text-ff-muted">{badges.intro}</p>
            <div className="mt-5"><BadgeGrid badges={badges.preview_badges} testid="sponsors-badge-grid" /></div>
            <p className="mt-4 text-xs text-ff-muted">{badges.disclaimer}</p>
          </section>
        )}

        <div className="mt-12 rounded-md border border-ff-brass/30 bg-ff-bg2 p-5 text-sm text-ff-muted" data-testid="sponsors-disclaimer">
          {data?.disclaimer}
        </div>

        <p className="mt-8 text-xs text-ff-muted">
          Interested sponsors: no campaigns, funding, or payments are accepted at this time. This page is a concept preview only.
        </p>
      </main>

      <footer className="border-t border-ff-brass/20 py-6 text-center text-xs text-ff-muted">
        Aurumward — a virtual expedition game. All Gold is in-game only.
      </footer>
    </div>
  );
}
